import * as React from "react";
import {inject, observer} from "mobx-react";
import type {IViewColumn, IViewSort} from "../../../data/models/ViewUtils";
import {SortDirection} from "../../../data/models/ViewUtils";
import type {View} from "../../../data/models/View";
import {QuickLinks} from "../../modules/abstract/common/tooltip/QuickLinks";
import type {IFieldPointer} from "../../../data/models/field/Field";
import type {XyiconFeature} from "../../../generated/api/base";
import {DomUtils} from "../../../utils/dom/DomUtils";
import {Functions} from "../../../utils/function/Functions";
import {LoaderIcon} from "../button/LoaderIcon";
import {NoResultSearchView} from "../../modules/abstract/grid/NoResultSearchView";
import {NoResultFilterView} from "../../modules/abstract/grid/NoResultFilterView";
import {FocusLoss} from "../../../utils/ui/focus/FocusLoss";
import type {IModel} from "../../../data/models/Model";
import type {Pointer} from "../../../utils/interaction/Pointer";
import type {App} from "../../../App";
import {ResizeDetector} from "../../../utils/resize/ResizeDetector";
import {TimeUtils} from "../../../utils/TimeUtils";
import {HTMLUtils} from "../../../utils/HTML/HTMLUtils";
import type {ManageColumnCallback} from "./TableHeaderDropDown";
import {TableHeaders} from "./TableHeaders";
import {TableRow} from "./TableRow";
import {TableFooter} from "./TableFooter";

export interface ITableProps<T> {
	readonly loading?: boolean;
	readonly errorMessage?: string;
	readonly getHeaders: () => Partial<IViewColumn>[]; // TODO view is not nice here
	readonly onHeaderWidthChange: (index: number, width: number, header: Partial<IViewColumn>) => void;
	readonly data: T[];
	readonly searchedData: T[];
	readonly getKey: (item: T, index: number) => string | number;
	readonly getFields: (item: T, index?: number) => (string | number)[];
	readonly getFieldsArray: (item: T, index?: number) => any;
	readonly selected?: T[];
	readonly onSelect: (items: T[], isGoToItemNeeded?: boolean) => void;
	readonly onClick?: (item: T) => void;
	readonly onDoubleClick?: (item: T) => void;
	readonly onDuplicateClick?: (item: T) => void;
	readonly onReorderColumn?: (index: number, newIndex: number) => void;
	readonly itemsPerPage?: number;
	readonly checkboxColumn?: boolean;
	readonly onToggleSort: (column: IFieldPointer) => void;
	readonly moduleName?: string;
	readonly feature?: XyiconFeature;
	readonly tableRowIcon?: boolean;
	readonly search?: string;
	readonly searching?: boolean;
	readonly filterSuppression?: boolean;
	readonly isCellContentsWrappingOn?: boolean;
	readonly onManageColumns?: ManageColumnCallback;
	readonly onTableRowMouseMove?: (pointer: Pointer, row: TableRow<T>) => void;
	readonly onSortAsc?: (id: IFieldPointer) => void;
	readonly onSortDesc?: (id: IFieldPointer) => void;
	readonly onClearSort?: (id: IFieldPointer) => void;
	readonly sorts: IViewSort[];
	readonly view: View;
	readonly app?: App;
}

interface ITableState<T> {
	lastSelectedItem: T;
	itemsPerPage: number;
	currentPage: number;
	isQuickLinksOpen: boolean;
	quickLinkXPos: number;
	quickLinkYPos: number;
	quickLinkParentId: number;
}

@inject("app")
@observer
export class Table<T> extends React.Component<ITableProps<T>, ITableState<T>> {
	public static readonly defaultProps = {
		checkboxColumn: true,
		sorts: [
			{
				column: "",
				direction: SortDirection.ASC,
			},
		],
		getKey: (item: IModel, index: number) => item?.id || index,
		getFilteredData: (items: IModel[]) => items,
		onSelect: Functions.emptyFunction,
	};

	private _header = React.createRef<HTMLDivElement>();
	public _body = React.createRef<HTMLTableSectionElement>();
	public _table = React.createRef<HTMLTableElement>();
	private _resizeDetector: ResizeDetector;
	private _isMounted = false;

	public container = React.createRef<HTMLDivElement>();

	private _hoveringModel: T;

	constructor(props: ITableProps<T>) {
		super(props);
		this.state = {
			lastSelectedItem: null,
			itemsPerPage: this.props.itemsPerPage || 100,
			currentPage: 0,
			isQuickLinksOpen: false,
			quickLinkXPos: 0,
			quickLinkYPos: 0,
			quickLinkParentId: null,
		};
	}

	private updateQuickLinksStates = (x: number, y: number) => {
		this.setState({
			quickLinkXPos: x,
			quickLinkYPos: y,
			isQuickLinksOpen: true,
		});
	};

	private showQuickLinks = (e: React.MouseEvent, item: T, index: number) => {
		e.preventDefault();
		FocusLoss.listen(this.props.app.modalContainer, this.onTableBlur);
		this.setState({quickLinkParentId: index});

		if (this.state.isQuickLinksOpen) {
			this.hideQuickLinks();
		}

		let x = e.pageX;
		let y = e.pageY;

		this._hoveringModel = item;
		this.updateQuickLinksStates(x, y);
	};

	private scrollTableToShowFullCell = (e: React.MouseEvent) => {
		const table = this._table.current;

		if (table) {
			const tableBoundingBox = table.getBoundingClientRect();
			const divBoundingBox = e.currentTarget.getBoundingClientRect();
			const currentScrollValue = table.scrollLeft;
			const overFlow = divBoundingBox.right - tableBoundingBox.right;
			const underflow = divBoundingBox.left - tableBoundingBox.left;

			if (overFlow > 0) {
				table.scroll({left: currentScrollValue + overFlow});
			} else if (underflow < 0) {
				// add some px to be prettier
				table.scroll({left: currentScrollValue + underflow});
			}
			this.onTableScroll(e);
		}
	};

	private hideQuickLinks = () => {
		if (this.state.isQuickLinksOpen) {
			this.setState({
				isQuickLinksOpen: false,
			});
		}
	};

	private onTableScroll = (event?: React.UIEvent | ResizeDetector) => {
		// This runs when the table is scrolled horizontally.
		// We need to set the width manually for header + body, to make sure the vertical scrollbar is position properly.
		// See:
		// https://stackoverflow.com/questions/48217171/horizontal-and-vertical-scroll-able-table-in-bootstrap

		const table = this._table.current;

		if (table) {
			const header = this._header.current;
			const body = this._body.current;

			const w = table.offsetWidth + table.scrollLeft;

			header.style.width = `${w}px`;
			body.style.width = `${w}px`;
		}
	};

	private onTableBlur = () => {
		this.hideQuickLinks();
	};

	public async goToItem(item: any) {
		this.goToPage(item);
		await TimeUtils.waitForNextFrame();
		this.scrollToItemId(item.id); // TODO any?
	}

	private goToPage(item: any) {
		// go to the page where the item is
		const pageIndex = this.getPageIndex(item);

		if (this.state.currentPage !== pageIndex) {
			this.setState({
				currentPage: pageIndex,
			});
		}
	}

	public scrollToItemId(id: string) {
		if (this._isMounted) {
			const grid = this.container.current;

			if (grid) {
				const selectedRow = grid.querySelector(`*[data-key="${id}"]`) as HTMLDivElement;

				DomUtils.scrollIntoViewIfNeeded(selectedRow);
			}
		}
	}

	private getPageIndex(item: any) {
		const {data} = this.props;
		const filteredData = this.props.searchedData || data;
		const {itemsPerPage} = this.state;

		const index = filteredData.indexOf(item);

		if (index === -1) {
			return 0;
		}
		return Math.floor(index / itemsPerPage);
	}

	private onAutoResizeColumn = (index: number, header: Partial<IViewColumn>) => {
		const container = this.container.current;

		if (!container) {
			return;
		}

		// Add a dummy table, measure the column width, remove dummy table
		const table = document.createElement("table");

		table.className = "Table";
		table.style.position = "absolute";
		const tbody = document.createElement("tbody");

		const firstTR = document.createElement("tr");

		firstTR.innerHTML = this.props.getHeaders()[index].title;
		tbody.appendChild(firstTR);

		for (const data of this.props.data) {
			const tr = document.createElement("tr");

			const fields = this.props.getFields(data, 0);
			const field = fields[index] || "";

			tr.innerHTML = field.toString();
			tbody.appendChild(tr);
		}

		table.appendChild(tbody);
		container.appendChild(table);

		const padding = 20;
		const iconSize = 18;
		const maxWidth = 700;
		const width = Math.min(maxWidth, firstTR.clientWidth + 2 * padding + iconSize);

		container.removeChild(table);

		this.onHeaderWidthChange(index, width, header);
	};

	private applyColumnWidth = (index: number, width: number) => {
		const table = this._table.current;

		if (!table) {
			return;
		}

		const widthPx = `${width}px`;

		table.querySelectorAll<HTMLElement>(`[data-table-column-index="${index}"]`).forEach((element) => {
			element.style.width = widthPx;
			element.style.minWidth = widthPx;
		});
	};

	private onHeaderWidthPreview = (index: number, width: number, header: Partial<IViewColumn>) => {
		this.applyColumnWidth(index, width);
		this.onTableScroll();
	};

	private onHeaderWidthChange = (index: number, width: number, header: Partial<IViewColumn>) => {
		this.applyColumnWidth(index, width);
		this.props.onHeaderWidthChange(index, width, header);
		this.onTableScroll();
	};

	private isAllSelected(filteredData: T[]) {
		return filteredData.length === this.props.selected?.length;
	}

	private onAllClicked = () => {
		const filteredData = this.props.searchedData || this.props.data || [];

		if (this.isAllSelected(filteredData)) {
			this.props.onSelect([], false);
		} else {
			this.props.onSelect(filteredData, false);
		}

		this.hideQuickLinks();
	};

	private onCheckboxRowClick = (event: React.MouseEvent, item: T) => {
		event.stopPropagation();

		this.hideQuickLinks();

		const index = this.props.selected.indexOf(item);

		if (index === -1) {
			if (event.shiftKey) {
				this.onShiftClick(item);
			} else {
				const selected = [...this.props.selected, item];

				this.props.onSelect(selected);
			}
		} else {
			const selected = this.props.selected.slice(0);

			selected.splice(index, 1);
			this.props.onSelect(selected, false);
		}

		if (!event.shiftKey && !this.props.selected.includes(item)) {
			this.setState({
				lastSelectedItem: item,
			});
		}
	};

	private onShiftClick = (item: T) => {
		const {data, selected, onSelect} = this.props;
		const lastIndex = data.indexOf(this.state.lastSelectedItem);
		const newIndex = data.indexOf(item);

		if (lastIndex > -1 && newIndex > -1) {
			if (selected?.includes(this.state.lastSelectedItem)) {
				const newSelection = [];
				const diff = newIndex - lastIndex;

				if (diff > 0) {
					for (let i = lastIndex; i <= newIndex; ++i) {
						newSelection.push(data[i]);
					}
				} else if (diff < 0) {
					for (let i = lastIndex; i >= newIndex; --i) {
						newSelection.push(data[i]);
					}
				}

				if (newSelection.length) {
					onSelect(newSelection);
				}
			} else {
				onSelect([item]);
			}
		}
	};

	private onSelectRow = (event: React.MouseEvent, item: T) => {
		const {selected} = this.props;
		const el = event.target as HTMLElement;
		const excludedCasses = ["quickLinksButton", "item", "label"];

		if (this.props.onClick) {
			this.props.onClick(item);
		}

		if (!excludedCasses.includes(el.className) || el.className.includes("disabled")) {
			if (event.type === "click") {
				this.hideQuickLinks();
			}

			if (event.metaKey || event.ctrlKey) {
				const newSelection = selected.slice(0);
				let isGoToItemNeeded = false;
				const itemIndex = selected.indexOf(item);

				if (itemIndex === -1) {
					newSelection.push(item);
					isGoToItemNeeded = true;
				} else {
					newSelection.splice(itemIndex, 1);
				}

				this.props.onSelect(newSelection, isGoToItemNeeded);
			} else if (event.shiftKey && this.state.lastSelectedItem) {
				this.onShiftClick(item);

				// Option B to remove selection (Option A is used now)
				// document.getSelection().removeAllRanges();
			} else {
				this.props.onSelect([item]);
			}

			if (!event.shiftKey && !selected.includes(item)) {
				this.setState({
					lastSelectedItem: item,
				});
			}
		}
	};

	public onPageChange = (page: number) => {
		this.setState({currentPage: page});
		this._body.current?.scroll({top: 0});
	};

	private getPagedData(data: T[]) {
		const {itemsPerPage, currentPage} = this.state;

		let pagedData = data;
		let startIndex = 0;
		let lastIndex = data.length - 1;

		if (itemsPerPage > 0) {
			startIndex = currentPage * itemsPerPage;

			if (data.length !== 0) {
				lastIndex = startIndex + itemsPerPage - 1;
				lastIndex = Math.min(data.length - 1, lastIndex);
			} else {
				lastIndex = 0;
			}

			pagedData = data.slice(startIndex, lastIndex + 1);
		}

		return {
			pagedData: pagedData,
			startIndex: startIndex,
			lastIndex: lastIndex,
		};
	}

	private onDuplicateClick = (item: T) => {
		this.setState({isQuickLinksOpen: false});
		this.props.onDuplicateClick?.(item);
	};

	public override componentDidMount(): void {
		const table = this._table.current;

		if (table) {
			this._resizeDetector = new ResizeDetector(table);
			this._resizeDetector.resize.add(this.onTableScroll, this);
		}

		this._isMounted = true;
	}

	public override componentDidUpdate(prevProps: ITableProps<T>) {
		const {search, view} = this.props;

		if (search !== prevProps.search || view !== prevProps.view) {
			this.setState({currentPage: 0});
		}
	}

	public override componentWillUnmount() {
		if (this._resizeDetector) {
			this._resizeDetector.resize.remove(this.onTableScroll, this);
			this._resizeDetector.dispose();
		}

		this._isMounted = false;
	}

	public override render() {
		let {getFieldsArray} = this.props;
		const {
			getKey,
			checkboxColumn,
			loading,
			searching,
			errorMessage,
			getFields,
			moduleName,
			tableRowIcon,
			filterSuppression,
			feature,
			view,
			onSortAsc,
			onSortDesc,
			onClearSort,
			sorts,
			isCellContentsWrappingOn,
			search,
			onDoubleClick,
			onTableRowMouseMove,
			searchedData,
			getHeaders,
			onToggleSort,
			onReorderColumn,
			onManageColumns,
		} = this.props;
		const {isQuickLinksOpen, quickLinkXPos, quickLinkYPos, quickLinkParentId} = this.state;

		getFieldsArray = getFieldsArray || getFields;

		const data = this.props.data || [];
		const filteredData = searchedData || data;
		const {pagedData, startIndex, lastIndex} = this.getPagedData(filteredData);

		const selected = this.props.selected || [];
		const allSelected = this.isAllSelected(filteredData);

		const headers = getHeaders();

		return (
			<div
				className={`tableContainer module-${moduleName}`}
				ref={this.container}
			>
				<div
					ref={this._table}
					className="Table"
					onScroll={this.onTableScroll}
				>
					<TableHeaders
						headerRef={this._header}
						headers={headers}
						checkboxColumn={checkboxColumn}
						allSelected={allSelected}
						onAllClicked={this.onAllClicked}
						onToggleSort={onToggleSort}
						onAutoResizeColumn={this.onAutoResizeColumn}
						onReorderColumn={onReorderColumn}
						onHeaderWidthChange={this.onHeaderWidthChange}
						onHeaderWidthPreview={this.onHeaderWidthPreview}
						tableRowIcon={tableRowIcon}
						onManageColumns={onManageColumns}
						feature={feature}
						sorts={sorts}
						view={view}
						onSortAsc={onSortAsc}
						onSortDesc={onSortDesc}
						onClearSort={onClearSort}
					/>
					<div
						ref={this._body}
						className={`body ${HTMLUtils.noFocusClassName}`}
					>
						{errorMessage ? (
							<div className="tableLoader">
								<div className="message">{errorMessage}</div>
							</div>
						) : loading || searching ? (
							<div className="tableLoader">
								<div className="message">{searching ? "Searching..." : "Fetching data..."}</div>
								<LoaderIcon />
							</div>
						) : pagedData.length === 0 && search?.length !== 0 ? (
							<NoResultSearchView search={search} />
						) : (
							filteredData.length === 0 && filterSuppression && <NoResultFilterView />
						)}
						<div className="tableWrapper">
							{pagedData.map((item, index) => {
								index = startIndex + index;
								const fields = getFieldsArray(item, index);
								const itemSelected = selected.includes(item);

								if (fields.length !== headers.length) {
									console.warn("Header and fields length don't match!");
								}

								const key = getKey(item, index);

								return (
									<TableRow
										key={key}
										item={item}
										id={key}
										fields={fields}
										headers={headers}
										index={index}
										selected={itemSelected}
										onSelect={this.onSelectRow}
										onDoubleClick={onDoubleClick}
										showQuickLinks={this.showQuickLinks}
										showCheckboxColumn={checkboxColumn}
										quickLinksActive={isQuickLinksOpen && quickLinkParentId == index}
										onCheckboxRowClick={this.onCheckboxRowClick}
										feature={feature}
										tableRowIcon={tableRowIcon}
										isCellContentsWrappingOn={isCellContentsWrappingOn}
										onMouseMove={onTableRowMouseMove}
										selectedItems={selected}
										scrollTableToShowFullCell={this.scrollTableToShowFullCell}
									/>
								);
							})}
							{
								// We need to pass portfolioID property, because we have to know which portfolio
								// links should appear in the QuickLinks component
								isQuickLinksOpen && (
									<QuickLinks
										xPos={quickLinkXPos}
										yPos={quickLinkYPos}
										hoveringModel={this._hoveringModel}
										onDuplicateClick={this.onDuplicateClick}
										feature={feature}
									/>
								)
							}
						</div>
					</div>
				</div>
				<TableFooter
					itemsPerPage={this.state.itemsPerPage}
					totalCount={filteredData.length}
					startIndex={startIndex}
					lastIndex={lastIndex}
					currentPage={this.state.currentPage}
					onPageChange={this.onPageChange}
				/>
			</div>
		);
	}
}
